import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Platform Admin Login Page
 * 
 * Separate login for platform administrators (NOT tenant users).
 * Platform admins have system-wide access to manage all tenants,
 * ML infrastructure, and platform settings.
 * 
 * Key differences from tenant login:
 * - Calls /api/platform-admin/auth/login (NOT /api/auth/token)
 * - JWT includes is_platform_admin=True claim
 * - Redirects to admin.astrapeak.ai (NOT tenant dashboard)
 * - No tenant_id in JWT (platform admins are NOT tenant-scoped)
 */
export const PlatformAdminLogin: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { login } = usePlatformAdminAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Use context login to ensure React state is updated synchronously
      // This fixes the race condition where localStorage was updated but
      // context state wasn't, causing ProtectedRoute to redirect back to login
      await login(username, password);

      // Redirect to platform admin dashboard
      // If we're already on admin.astrapeak.ai, go to dashboard
      // Otherwise, redirect to admin.astrapeak.ai
      const hostname = window.location.hostname;
      if (hostname === 'admin.astrapeak.ai' || hostname === 'localhost') {
        navigate('/platform-admin/dashboard');
      } else {
        // Redirect to admin.astrapeak.ai
        window.location.href = 'https://admin.astrapeak.ai/platform-admin/dashboard';
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid username or password. Please try again.';
      setError(errorMessage);
      console.error('Platform admin login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 p-8 bg-gray-800 rounded-lg shadow-lg">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-white">AstraPeak Platform Admin</h1>
          <p className="mt-2 text-gray-400">System Administrator Access</p>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md 
                           text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                           focus:ring-purple-500 focus:border-transparent"
                placeholder="admin-username"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md 
                             text-white placeholder-gray-400 focus:outline-none focus:ring-2 
                             focus:ring-purple-500 focus:border-transparent"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md 
                         shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in as Platform Admin'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-400">
              Tenant user?{' '}
              <a
                href="/"
                className="font-medium text-purple-400 hover:text-purple-300"
              >
                Sign in to your tenant dashboard
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformAdminLogin;
